import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.tabIndex === -1) return false;
    if (el.closest("[inert]")) return false;
    const style = getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
  });

type UseFocusTrapOptions<T extends HTMLElement> = {
  isActive: boolean;
  containerRef: React.RefObject<T | null>;
  mountedSignal?: unknown;
};

export const useFocusTrap = <T extends HTMLElement>({
  isActive,
  containerRef,
  mountedSignal,
}: UseFocusTrapOptions<T>) => {
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    previouslyFocusedRef.current = document.activeElement;
    container.focus();
  }, [isActive, containerRef, mountedSignal]);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusable = getFocusableElements(containerRef.current as HTMLElement);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (e.shiftKey) {
        if (activeElement === first) {
          e.preventDefault();
          last.focus();
        }

        return;
      }

      if (activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, containerRef, mountedSignal]);

  // Restore focus when the trap deactivates (the container may stay mounted) or on unmount.
  useEffect(() => {
    if (isActive) return;

    (previouslyFocusedRef.current as HTMLElement | null)?.focus();
    previouslyFocusedRef.current = null;
  }, [isActive]);

  useEffect(() => {
    return () => {
      (previouslyFocusedRef.current as HTMLElement | null)?.focus();
      previouslyFocusedRef.current = null;
    };
  }, []);
};
